import { useClipboard } from "@vueuse/core";

export function useCertificate () {
  const { copy, isSupported } = useClipboard();
  const { dispatchMessage } = useSystemMessages();

  const isPreparingCertificateForLinkedIn = ref<boolean>(false);

  const downloadBasicCertificate = async (courseId: number) => {
    const url = useApiUrl('/courses/' + courseId + '/claim-certificate/basic');
    return useAuthenticatedFetch(url, { method: 'POST' });
  };

  const downloadClaimedCertificate = async (courseId: number) => {
    const url = useApiUrl('/courses/' + courseId + '/claim-certificate/claimed');
    return useAuthenticatedFetch(url, { method: 'POST' });
  };

  const submitClaimCertificateForm = async (
    courseId: number,
    body: {},
  ) => {
    const url = useApiUrl('/courses/' + courseId + '/claim-certificate/cme');
    return await useAuthenticatedFetch(url, { method: 'POST', body });
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  };

  const shareCertificateOnLinkedIn = async (courseId?: number) => {
    isPreparingCertificateForLinkedIn.value = true;

    return await useErrorHandling(async () => {
      if (!courseId) {
        throw new Error('Course ID is required to share the certificate.');
      }

      const url = useApiUrl(`/courses/${courseId}/certificate/share`);
      const response = await $fetch<{
        url: string;
      }>(url);

      if (!response.url) {
        throw new Error("Couldn't share the certificate.");
      }

      if (isValidUrl(response.url)) {
        if (isSupported) {
          copy(response.url);
          dispatchMessage('Success!', 'success', 'Link copied to clipboard');
        }
        window.open(response.url, '_blank');
      } else {
        throw new Error('Invalid url to share the certificate.');
      }
    }).finally(() => {
      isPreparingCertificateForLinkedIn.value = false;
    });
  };

  const startDownload = (data: BlobPart, fileName: string = 'certificate.pdf') => {
    const blob = new Blob([data], { type: 'application/pdf' });
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = downloadUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return {
    downloadBasicCertificate,
    downloadClaimedCertificate,
    submitClaimCertificateForm,
    startDownload,
    shareCertificateOnLinkedIn,
    isPreparingCertificateForLinkedIn,
  };
}